// Validation for session forms. Ranges are wide enough for real clinical values but catch typos
// (e.g. 1400 for 140). These are input checks, not clinical advice.
import { z } from 'zod';
import { optionalNumber, optionalText, optionalTime } from '../../lib/forms';

export const weightKg = optionalNumber(20, 300, 'Enter a weight between 20 and 300 kg.');
export const systolic = optionalNumber(50, 260, 'Top number between 50 and 260.');
export const diastolic = optionalNumber(30, 160, 'Bottom number between 30 and 160.');
export const pulse = optionalNumber(30, 220, 'Pulse between 30 and 220.');
export const temperature = optionalNumber(34, 42, 'Temperature between 34 and 42 °C.');
export const bloodSugar = optionalNumber(20, 600, 'Blood sugar between 20 and 600 mg/dL.');
export const fluidMl = optionalNumber(0, 8000, 'Enter mL between 0 and 8000.');

export const bpFields = { bp_systolic: systolic, bp_diastolic: diastolic, pulse };

/** Both blood-pressure numbers or neither, and the top one higher. */
export function checkBloodPressure(values: { bp_systolic: number | null; bp_diastolic: number | null }, ctx: z.RefinementCtx) {
  const { bp_systolic: sys, bp_diastolic: dia } = values;
  if ((sys == null) !== (dia == null)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [sys == null ? 'bp_systolic' : 'bp_diastolic'], message: 'Enter both blood pressure numbers.' });
  } else if (sys != null && dia != null && sys <= dia) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['bp_diastolic'], message: 'The bottom number should be lower than the top.' });
  }
}

export const sessionDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Choose a date.')
  .refine((value) => value <= localToday(), 'Sessions can’t be logged in the future.');

export function localToday() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export const beforeSchema = z
  .object({
    session_date: sessionDate,
    modality: z.enum(['in_center', 'home_hd']),
    start_time: optionalTime,
    pre_weight_kg: weightKg,
    uf_goal_ml: fluidMl,
    ...bpFields,
    temperature_c: temperature,
    blood_sugar_mg_dl: bloodSugar
  })
  .superRefine(checkBloodPressure);

export const intraSchema = z
  .object({
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a time like 09:30.'),
    ...bpFields,
    notes: optionalText(200)
  })
  .superRefine(checkBloodPressure)
  .refine((values) => values.bp_systolic != null || values.pulse != null || values.notes != null, {
    message: 'Enter a reading or a note.',
    path: ['bp_systolic']
  });

export const machineSchema = z.object({
  blood_flow_ml_min: optionalNumber(50, 600, 'Blood flow between 50 and 600 mL/min.'),
  dialysate_flow_ml_min: optionalNumber(100, 1000, 'Dialysate flow between 100 and 1000 mL/min.'),
  dialyzer: optionalText(60),
  heparin_dose: optionalText(60),
  needle_gauge: optionalText(20),
  venous_pressure: optionalNumber(0, 500, 'Venous pressure between 0 and 500 mmHg.'),
  arterial_pressure: z
    .string()
    .trim()
    .refine((value) => value === '' || (/^-?\d+$/.test(value) && Number(value) >= -400 && Number(value) <= 200), 'Arterial pressure between −400 and 200 mmHg.')
    .transform((value) => (value === '' ? null : Number(value))),
  tmp: optionalNumber(0, 500, 'TMP between 0 and 500 mmHg.'),
  conductivity: optionalNumber(10, 20, 'Conductivity between 10 and 20 mS/cm.')
});

export const afterSchema = z
  .object({
    end_time: optionalTime,
    post_weight_kg: weightKg,
    uf_achieved_ml: fluidMl,
    ...bpFields,
    outcome: z.enum(['completed', 'ended_early']),
    early_end_reason: optionalText(200),
    notes: optionalText(1000)
  })
  .superRefine(checkBloodPressure)
  .refine((values) => values.outcome !== 'ended_early' || values.early_end_reason != null, {
    message: 'Say briefly why it ended early.',
    path: ['early_end_reason']
  });
